import React from "react";
import { motion } from "framer-motion";
import { 
  Smartphone, 
  CalendarDays, 
  Calculator, 
  UserCheck, 
  Target, 
  Zap, 
  ShieldCheck, 
  Users, 
  TrendingUp,
  Leaf,
  Award,
  Clock,
  BarChart3,
  CheckCircle2,
  ArrowRight
} from "lucide-react";

const About = () => {
  const stats = [
    { icon: Users, label: "Distribuidores Activos", value: "500", suffix: "+", color: "text-emerald-600" },
    { icon: ShieldCheck, label: "Consultas de DNI", value: "10k", suffix: "+", color: "text-amber-600" },
    { icon: CalendarDays, label: "Eventos Gestionados", value: "120", suffix: "+", color: "text-teal-600" },
    { icon: Zap, label: "Precisión en Cálculos", value: "100", suffix: "%", color: "text-rose-600" }
  ];

  const features = [
    {
      icon: Smartphone,
      title: "Catálogo Digital Interactivo",
      text: "App y sistema web con todo el portafolio DXN. Muestra productos, beneficios y precios de forma profesional a tus prospectos en cualquier momento.",
      color: "emerald"
    },
    {
      icon: CalendarDays,
      title: "Gestión de Eventos",
      text: "Organiza, programa y da seguimiento a tus reuniones de equipo, zooms o presentaciones presenciales desde una sola plataforma centralizada.",
      color: "amber"
    },
    {
      icon: Calculator,
      title: "Calculadora de PV y SV",
      text: "Conoce al instante los Puntos de Volumen y Valor de tus pedidos. Planifica tus compras para alcanzar y mantener tus rangos en la red de manera estratégica.",
      color: "teal"
    },
    {
      icon: UserCheck,
      title: "Verificador de DNI",
      text: "Consulta en segundos si un posible socio ya está afiliado en la red. Evita duplicidades, ahorra tiempo y enfócate en prospectos reales.",
      color: "rose"
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: "Más Ventas",
      desc: "Presentación profesional que cierra más negocios",
      stat: "+40%"
    },
    {
      icon: Clock,
      title: "Menos Tiempo",
      desc: "Automatización de tareas repetitivas",
      stat: "-60%"
    },
    {
      icon: Award,
      title: "Más Rangos",
      desc: "Distribuidores que alcanzan metas más rápido",
      stat: "3x"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      emerald: { 
        bg: "bg-emerald-50", 
        border: "border-emerald-200", 
        text: "text-emerald-700", 
        iconBg: "bg-emerald-100", 
        iconText: "text-emerald-600", 
        gradient: "from-emerald-500 to-teal-500",
        hoverShadow: "hover:shadow-emerald-900/10" 
      },
      amber: { 
        bg: "bg-amber-50", 
        border: "border-amber-200", 
        text: "text-amber-700", 
        iconBg: "bg-amber-100", 
        iconText: "text-amber-600",
        gradient: "from-amber-500 to-orange-500",
        hoverShadow: "hover:shadow-amber-900/10" 
      },
      teal: { 
        bg: "bg-teal-50", 
        border: "border-teal-200", 
        text: "text-teal-700", 
        iconBg: "bg-teal-100", 
        iconText: "text-teal-600",
        gradient: "from-teal-500 to-cyan-500",
        hoverShadow: "hover:shadow-teal-900/10" 
      },
      rose: { 
        bg: "bg-rose-50", 
        border: "border-rose-200", 
        text: "text-rose-700", 
        iconBg: "bg-rose-100", 
        iconText: "text-rose-600",
        gradient: "from-rose-500 to-pink-500",
        hoverShadow: "hover:shadow-rose-900/10" 
      }
    };
    return colors[color] || colors.emerald;
  };

  return (
    <section id="nosotros" className="py-24 lg:py-32 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-amber-50/30 relative overflow-hidden">
      
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-emerald-100/10 to-amber-100/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* 🔹 Encabezado Mejorado */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-20 max-w-4xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-white border border-emerald-200 rounded-full px-5 py-2 shadow-lg shadow-emerald-100/50 mb-6"
          >
            <Leaf className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">Tecnología para tu Red DXN</span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 mb-6 leading-tight tracking-tight">
            Impulsamos tu Crecimiento con{" "}
            <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 bg-clip-text text-transparent">
              Mi DXN
            </span>
          </h2>
          
          <p className="text-lg md:text-xl text-stone-600 leading-relaxed">
            Diseñamos soluciones tecnológicas exclusivas para distribuidores independientes. 
            Simplificamos la gestión de tu negocio, desde la presentación de productos hasta 
            el seguimiento de tu equipo, para que te enfoques en lo que realmente importa: <strong className="text-stone-800">construir tu red.</strong>
          </p>
        </motion.div>

        {/* 🔹 Stats Grid */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6 mb-20"
        >
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + idx * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-stone-200 shadow-lg hover:shadow-xl transition-all text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl mb-3">
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="text-3xl font-bold text-stone-900 mb-1">{stat.value}<span className={stat.color}>{stat.suffix}</span></div>
                <div className="text-sm text-stone-600 font-medium">{stat.label}</div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* 🔹 Contenido Principal: Imagen + Descripción */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-28"
        >
          {/* Imagen / Mockup Mejorado */}
          <motion.div variants={itemVariants} className="relative order-2 lg:order-1">
            {/* Elementos flotantes */}
            <motion.div 
              animate={{ y: [0, -10, 0], rotate: [0, 3, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-6 -left-6 z-20 bg-white rounded-2xl shadow-xl p-4 border border-emerald-100 hidden lg:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">+50%</div>
                  <div className="text-xs text-stone-500">Productividad</div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              animate={{ y: [0, 10, 0], rotate: [0, -3, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -bottom-6 -right-6 z-20 bg-white rounded-2xl shadow-xl p-4 border border-amber-100 hidden lg:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">500+</div>
                  <div className="text-xs text-stone-500">Usuarios</div>
                </div>
              </div>
            </motion.div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-stone-900/20 border border-stone-200 bg-white aspect-[4/3] lg:aspect-[4/4]">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1000&auto=format&fit=crop"
                alt="Dashboard Mi DXN"
                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Smartphone className="w-5 h-5 text-emerald-400" />
                  <span className="font-semibold">Plataforma Mi DXN</span>
                </div>
                <p className="text-sm text-stone-200">Todo lo que necesitas para gestionar tu negocio en un solo lugar.</p>
              </div>
            </div>
          </motion.div>

          {/* Texto y Beneficios */}
          <motion.div variants={itemVariants} className="space-y-8 order-1 lg:order-2">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-6">
                La tecnología que tu equipo{" "}
                <span className="text-emerald-600">necesita para destacar</span>
              </h3>
              
              <div className="space-y-5">
                <p className="text-stone-600 text-lg leading-relaxed">
                  Sabemos que el tiempo es oro en el mercadeo en red. Por eso, hemos desarrollado un ecosistema digital que <strong className="text-stone-800">automatiza tareas repetitivas</strong> y profesionaliza tu presentación.
                </p>
                
                <p className="text-stone-600 text-lg leading-relaxed">
                  Olvídate de las hojas de cálculo manuales o de perder prospectos por no saber si ya están afiliados. Nuestra plataforma te da el control total de tu operación DXN.
                </p>
              </div>
            </div>

            {/* Stats inline mejorados */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="text-center p-5 bg-white rounded-2xl shadow-lg border border-stone-100 hover:border-emerald-200 transition-all hover:shadow-xl">
                <div className="text-3xl font-bold text-emerald-600 mb-1">24/7</div>
                <div className="text-sm text-stone-600 font-medium">Plataforma Disponible</div>
              </div>
              <div className="text-center p-5 bg-white rounded-2xl shadow-lg border border-stone-100 hover:border-amber-200 transition-all hover:shadow-xl">
                <div className="text-3xl font-bold text-amber-600 mb-1">100%</div>
                <div className="text-sm text-stone-600 font-medium">Datos Seguros</div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* 🔹 Beneficios Rápidos */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-6 mb-20"
        >
          {benefits.map((benefit, idx) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100 text-center"
              >
                <div className="text-4xl font-bold text-emerald-600 mb-2">{benefit.stat}</div>
                {/* AQUÍ ESTABA EL ERROR, YA CORREGIDO: </h4> en lugar de </div> */}
                <h4 className="font-bold text-stone-900 mb-1">{benefit.title}</h4>
                <p className="text-sm text-stone-600">{benefit.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* 🔹 Misión & Visión Mejoradas */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-8 mb-28"
        >
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-3xl transform group-hover:scale-[1.02] transition-transform duration-500 opacity-95" />
            <div className="relative p-8 lg:p-10 bg-white rounded-3xl h-full border border-stone-100">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-4">Nuestra Misión</h3>
              <p className="text-stone-600 leading-relaxed text-lg">
                Empoderar a los distribuidores de DXN con herramientas tecnológicas intuitivas y poderosas, que simplifiquen la gestión diaria de su negocio y aceleren su camino hacia el éxito en la red.
              </p>
            </div>
          </div>

          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-500 rounded-3xl transform group-hover:scale-[1.02] transition-transform duration-500 opacity-95" />
            <div className="relative p-8 lg:p-10 bg-white rounded-3xl h-full border border-stone-100">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-4">Nuestra Visión</h3>
              <p className="text-stone-600 leading-relaxed text-lg">
                Ser la plataforma tecnológica de referencia para los distribuidores de bienestar en Latinoamérica, combinando la esencia natural de DXN con la innovación digital de vanguardia.
              </p>
            </div>
          </div>
        </motion.div>

        {/* 🔹 Características Principales Mejoradas */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-28"
        >
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-white border border-emerald-200 rounded-full px-5 py-2 shadow-sm mb-6"
            >
              <Smartphone className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-700">Funcionalidades Principales</span>
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              Soluciones Diseñadas para <span className="text-emerald-600">Tu Éxito</span>
            </h3>
            <p className="text-stone-600 text-lg max-w-2xl mx-auto">
              Cuatro pilares tecnológicos que transforman la manera en que gestionas y haces crecer tu organización.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const colors = getColorClasses(feature.color);
              
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                  className={`group p-6 bg-white rounded-2xl shadow-lg hover:shadow-2xl border ${colors.border} ${colors.hoverShadow} transition-all duration-300`}
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${colors.gradient} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-stone-900 mb-3">{feature.title}</h4>
                  <p className="text-stone-600 text-sm leading-relaxed">{feature.text}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* 🔹 CTA Intermedio 
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-28"
        >
          <div className="bg-gradient-to-br from-emerald-800 via-teal-800 to-emerald-900 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/5 to-transparent" />
            
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">¿Listo para transformar tu negocio?</h3>
              <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
                Únete a los cientos de distribuidores que ya usan Mi DXN para potenciar sus resultados.
              </p>
              <button className="bg-white text-emerald-900 px-8 py-4 rounded-xl font-bold hover:bg-emerald-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 inline-flex items-center gap-2">
                Comenzar Ahora
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>*/}

        {/* 🔹 Estadísticas Detalladas */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
            Impacto en <span className="text-emerald-600">Cifras</span>
          </h3>
          <p className="text-stone-600 text-lg mb-12 max-w-2xl mx-auto">
            Resultados tangibles que reflejan la confianza de nuestra red de distribuidores.
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.15 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="p-6 lg:p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl border border-stone-100 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl flex items-center justify-center mx-auto mb-4 border border-emerald-100">
                    <Icon className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div className="text-3xl lg:text-4xl font-bold text-stone-900 mb-2">
                    {stat.value}<span className="text-emerald-600">{stat.suffix}</span>
                  </div>
                  <p className="text-stone-600 font-medium text-sm">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default About;